/**
 * The phone's GPS, and what it is good for next to the car's own speed.
 *
 * The vehicle speed PID is fast, never drops out and works in tunnels, but it
 * is a wheel-speed reading: whole km/h, and only as right as the tyre size the
 * car assumes -- typically a few percent out, and it changes with wear and
 * pressure. GPS ground speed is the opposite: about once a second, gone in a
 * car park, but with no scale error.
 *
 * So each does what it is good at. During steady driving with a good fix, GPS
 * teaches a correction factor for the car's speed; the car's speed scaled by
 * that factor is the road speed the rest of the app uses, and distance is
 * integrated from it. A pure GPS track distance is kept alongside as an
 * independent cross-check.
 */

export interface GpsFix {
  /** Epoch ms at which the position was determined. */
  t: number;
  lat: number;
  lon: number;
  /** Horizontal accuracy radius in metres, as the platform reports it. */
  accuracyM: number;
  /** Ground speed from the receiver in m/s, when it provides one. */
  speedMps?: number | null;
}

/** Accumulated evidence for the speed correction. */
export interface SpeedCalibration {
  /** Sum of GPS speed × seconds, over samples that qualified. */
  gps: number;
  /** Sum of the car's speed × seconds, over the same samples. */
  obd: number;
  /** Seconds of qualifying driving behind the sums. */
  seconds: number;
}

export interface ObdSpeedContext {
  /** The car's reported speed at the moment of the fix, km/h. */
  kph?: number;
  /** Longitudinal acceleration in m/s², for the steady-driving test. */
  accel?: number;
}

const EARTH_RADIUS_M = 6_371_008.8;

/** Great-circle distance between two points, in metres. */
export function haversineM(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const rad = Math.PI / 180;
  const dLat = (bLat - aLat) * rad;
  const dLon = (bLon - aLon) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(aLat * rad) * Math.cos(bLat * rad) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Fixes vaguer than this are not used to draw the track. */
const MAX_TRACK_ACCURACY_M = 25;
/** ...nor to calibrate speed. */
const MAX_CAL_ACCURACY_M = 20;
/**
 * Calibration only uses speeds where a whole-km/h reading is a small
 * fraction, and where GPS speed noise is small relative to the speed.
 */
const MIN_CAL_KPH = 30;
/** GPS speed lags by up to a second, so only steady driving is compared. */
const MAX_CAL_ACCEL = 0.5;
/** A reading this far from the car's is a bad fix, not a calibration error. */
const MAX_CAL_DISAGREEMENT = 0.15;
/** Evidence needed before the correction is applied. */
const MIN_CAL_SECONDS = 30;
/**
 * Older evidence fades beyond this much, so a change of tyres is picked up
 * within a drive or two.
 */
const MAX_CAL_SECONDS = 1800;
/** Bounds for a believable correction. */
const FACTOR_RANGE = [0.85, 1.15] as const;
/** A straight line between fixes further apart than this is not the road. */
const MAX_TRACK_GAP_S = 60;
const MAX_PLAUSIBLE_MPS = 90;
/** Below this the car counts as stationary and position jitter is ignored. */
const MOVING_KPH = 3;

export class GpsTracker {
  /** Track distance this trip, metres. */
  distanceM = 0;
  /** Latest ground speed, km/h. */
  speedKph: number | undefined;
  /** True when `speedKph` came from the receiver rather than from positions. */
  speedMeasured = false;
  last: GpsFix | undefined;
  private anchor: GpsFix | undefined;
  private cal: SpeedCalibration = { gps: 0, obd: 0, seconds: 0 };

  /**
   * Correction for the car's speed reading: multiply by this to get ground
   * speed. Undefined until there is enough evidence.
   */
  get factor(): number | undefined {
    if (this.cal.seconds < MIN_CAL_SECONDS || this.cal.obd <= 0) return undefined;
    const f = this.cal.gps / this.cal.obd;
    return f >= FACTOR_RANGE[0] && f <= FACTOR_RANGE[1] ? f : undefined;
  }

  /** Seconds of steady driving the correction is based on. */
  get calibrationSeconds(): number {
    return this.cal.seconds;
  }

  ingest(fix: GpsFix, obd: ObdSpeedContext = {}): void {
    if (![fix.t, fix.lat, fix.lon].every(Number.isFinite)) return;
    const prev = this.last;
    if (prev && fix.t <= prev.t) return; // delivered twice
    this.last = fix;
    const accuracy = Number.isFinite(fix.accuracyM) ? fix.accuracyM : Infinity;
    const stepS = prev ? (fix.t - prev.t) / 1000 : 0;

    // ------------------------------------------------------------- speed
    const reported = fix.speedMps;
    if (typeof reported === 'number' && Number.isFinite(reported) && reported >= 0) {
      this.speedKph = reported * 3.6;
      this.speedMeasured = true;
    } else if (
      prev && stepS >= 0.5 && stepS <= 10 &&
      accuracy <= MAX_TRACK_ACCURACY_M && prev.accuracyM <= MAX_TRACK_ACCURACY_M
    ) {
      this.speedKph = (haversineM(prev.lat, prev.lon, fix.lat, fix.lon) / stepS) * 3.6;
      this.speedMeasured = false;
    } else {
      this.speedKph = undefined;
      this.speedMeasured = false;
    }

    this.track(fix, accuracy, obd.kph);

    // --------------------------------------------------------- calibration
    // Only a receiver-measured speed is good enough: a speed differenced from
    // positions is biased by position noise by about as much as the error it
    // would be correcting.
    const gpsKph = this.speedKph;
    const obdKph = obd.kph;
    if (
      this.speedMeasured && gpsKph !== undefined && obdKph !== undefined &&
      accuracy <= MAX_CAL_ACCURACY_M &&
      gpsKph >= MIN_CAL_KPH && obdKph >= MIN_CAL_KPH &&
      Math.abs(obd.accel ?? 0) <= MAX_CAL_ACCEL &&
      Math.abs(gpsKph / obdKph - 1) <= MAX_CAL_DISAGREEMENT
    ) {
      const w = prev ? Math.max(0.2, Math.min(stepS, 2)) : 1;
      const c = this.cal;
      c.gps += gpsKph * w;
      c.obd += obdKph * w;
      c.seconds += w;
      if (c.seconds > MAX_CAL_SECONDS) {
        const keep = MAX_CAL_SECONDS / c.seconds;
        c.gps *= keep;
        c.obd *= keep;
        c.seconds = MAX_CAL_SECONDS;
      }
    }
  }

  /**
   * Add up the track. Stationary jitter is never counted, a vague fix is
   * skipped rather than trusted, and a gap too long to bridge with a straight
   * line starts a new segment instead of cutting the corner.
   */
  private track(fix: GpsFix, accuracy: number, obdKph: number | undefined): void {
    if (accuracy > MAX_TRACK_ACCURACY_M) return;
    const a = this.anchor;
    if (!a) {
      this.anchor = fix;
      return;
    }
    const dt = (fix.t - a.t) / 1000;
    if (dt <= 0) return;
    const d = haversineM(a.lat, a.lon, fix.lat, fix.lon);
    if (dt > MAX_TRACK_GAP_S || d / dt > MAX_PLAUSIBLE_MPS) {
      this.anchor = fix;
      return;
    }

    const kph = obdKph ?? (this.speedMeasured ? this.speedKph : undefined);
    if (kph !== undefined) {
      if (kph < MOVING_KPH) {
        this.anchor = fix;
        return;
      }
    } else if (d <= Math.max(accuracy, a.accuracyM)) {
      // No speed to go by: wait until the movement is bigger than the fixes
      // are uncertain, keeping the anchor where it is.
      return;
    }
    this.distanceM += d;
    this.anchor = fix;
  }

  resetTrip(): void {
    this.distanceM = 0;
    this.anchor = undefined;
  }

  exportCalibration(): SpeedCalibration {
    return { ...this.cal };
  }

  importCalibration(c: SpeedCalibration | undefined): void {
    const ok =
      typeof c === 'object' && c !== null &&
      [c.gps, c.obd, c.seconds].every((v) => typeof v === 'number' && Number.isFinite(v) && v >= 0);
    this.cal = ok ? { gps: c.gps, obd: c.obd, seconds: Math.min(c.seconds, MAX_CAL_SECONDS) } : { gps: 0, obd: 0, seconds: 0 };
  }

  resetCalibration(): void {
    this.cal = { gps: 0, obd: 0, seconds: 0 };
  }
}
